import type { CSSProperties, FunctionComponent, SVGProps } from 'react'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChartLine,
  faColumns,
  faHandshake,
  faChartPie,
  faExchangeAlt,
  faCircleDot,
  faBuilding,
  faAddressBook,
  faDollarSign,
  faCogs,
  faGear,
  faDownload,
  faFile,
  faFileLines,
  faFileInvoiceDollar,
  faBook,
  faList,
  faPaperPlane,
  faEnvelope,
  faSignature,
  faRobot,
  faLayerGroup,
  faTags,
  faBolt,
  faDiagramProject,
  faFileContract,
  faIndustry,
  faSatelliteDish,
  faPlug,
  faUsersGear,
  faUsers,
  faUserShield,
  faPenToSquare,
  faInbox,
  faPeopleArrows,
  faLink,
  faPercent
} from '@fortawesome/free-solid-svg-icons'

import { ReactComponent as FundsIcon } from '../assets/icons/fundsIcon.svg'
import { CONFIG_MODULES } from '../app_modules/configuration/configModules.ts'

export type AppIcon =
  | { kind: 'fa'; def: IconDefinition }
  | { kind: 'svg'; Component: FunctionComponent<SVGProps<SVGSVGElement>> }

const FA_ICONS: Record<string, IconDefinition> = {
  faChartLine,
  faColumns,
  faHandshake,
  faChartPie,
  faExchangeAlt,
  faCircleDot,
  faBuilding,
  faAddressBook,
  faDollarSign,
  faCogs,
  faGear,
  faDownload,
  faFile,
  faFileLines,
  faFileInvoiceDollar,
  faBook,
  faList,
  faPaperPlane,
  faEnvelope,
  faSignature,
  faRobot,
  faLayerGroup,
  faTags,
  faBolt,
  faDiagramProject,
  faFileContract,
  faIndustry,
  faSatelliteDish,
  faPlug,
  faUsersGear,
  faUsers,
  faUserShield,
  faPenToSquare,
  faInbox,
  faPeopleArrows,
  faLink,
  faPercent
}

/**
 * Maps app module keys → icon keys. Used by TabNavigation and any other
 * surface that needs the canonical icon for a module. Keep in sync with
 * cfg.menu_items.icon values where the same modules appear.
 */
export const MODULE_ICON_KEYS: Record<string, string> = {
  'deal-pipeline': 'faColumns',
  'deal-execution': 'faColumns',
  deals: 'faHandshake',
  companies: 'faBuilding',
  contacts: 'faAddressBook',
  fundraising: 'faDollarSign',
  'fundraising-pipeline': 'faColumns',
  'asset-transactions': 'faExchangeAlt',
  dashboard: 'faChartLine',
  lookups: 'faList',
  rounds: 'faCircleDot',
  tranches: 'faLayerGroup',
  downloads: 'faDownload',
  reports: 'faFileLines',
  funds: 'fundsIcon',
  investments: 'faChartPie',
  email: 'faEnvelope',
  'email-settings': 'faSignature',
  'agent-access': 'faRobot',
  // Personal agent screen (Fund Management → MY WORKSPACE).
  'my-agents': 'faRobot',
  // LP Allocation (Fundraising) and My Drafts (MY WORKSPACE).
  'lp-allocation': 'faUsersGear',
  // FUND MANAGEMENT → LPs.
  lps: 'faUsers',
  'my-drafts': 'faPenToSquare',
  // Reporting → Period Highlights (LP agent programme Phase 1).
  'period-highlights': 'faStar',
  configuration: 'faGear',
  'configuration-operations': 'faGear',
  'fund-transactions': 'faExchangeAlt',
  'capital-calls': 'faFileInvoiceDollar',
  distributions: 'faHandHoldingDollar',
  'journal-entries': 'faBook',
  'investor-statements': 'faFileContract',
  'fee-booking': 'faFileInvoiceDollar',
  'interest-accruals': 'faPercent',
  agents: 'faRobot',
  'capital-call-position': 'faChartPie',
  // Configuration AREA: one entry per config panel, generated from the
  // registry so panel icons cannot drift from cfg.menu_items.icon seeds.
  ...Object.fromEntries(CONFIG_MODULES.map((m) => [m.moduleKey, m.iconKey]))
}

export function getIconForModule(moduleKey: string): AppIcon {
  return getIcon(MODULE_ICON_KEYS[moduleKey] ?? 'faFile')
}

const SVG_ICONS: Record<string, FunctionComponent<SVGProps<SVGSVGElement>>> = {
  fundsIcon: FundsIcon
}

export function getIcon(iconKey: string): AppIcon {
  if (iconKey && SVG_ICONS[iconKey]) {
    return { kind: 'svg', Component: SVG_ICONS[iconKey] }
  }
  const def = FA_ICONS[iconKey] ?? faCircleDot
  return { kind: 'fa', def }
}

export function renderAppIcon(icon: AppIcon, style?: CSSProperties) {
  if (icon.kind === 'fa') {
    return <FontAwesomeIcon icon={icon.def} style={style} />
  }
  const { Component } = icon
  return <Component style={{ width: '1em', height: '1em', ...style }} />
}
